/**
 * DiligenceGPT site configuration and approved copy.
 * One conversion goal: BOOK A DEMO → DEMO_URL.
 */

export const DEMO_URL = 'https://demo.startupfuel.com'

export const BRAND = {
  product: 'DiligenceGPT™',
  tagline: 'Diligence at the Speed of Dealflow.',
  category: 'The Intelligence Layer for Venture Capital.',
  campaign: 'Where Vibe Coding Stops. DiligenceGPT Begins.',
} as const

export const VIDEO = {
  id: '1238409079',
  title: 'DiligenceGPT™: Venture Capital',
  /** Vimeo embed URL — loaded only after the visitor presses Play. */
  embedUrl:
    'https://player.vimeo.com/video/1238409079?badge=0&autopause=0&player_id=0&app_id=58479',
  /** Vimeo player API — loaded only after Play, for progress analytics. */
  playerApi: 'https://player.vimeo.com/api/player.js',
  /** Vimeo oEmbed endpoint used to resolve a high-resolution poster frame. */
  oembedUrl: 'https://vimeo.com/api/oembed.json?url=https%3A%2F%2Fvimeo.com%2F1238409079&width=1920',
  /**
   * Optional custom 1920×1080+ WebP exported from the video. When set, it is
   * used instead of the Vimeo thumbnail (and no oEmbed request is made).
   */
  posterLocal: '' as string,
  posterWidth: 1920,
  posterHeight: 1080,
} as const

export const NAV_LINKS = [
  { label: 'Platform', href: '#platform' },
  { label: 'Intelligence', href: '#intelligence' },
  { label: 'Dealflow', href: '#dealflow' },
  { label: 'Security', href: '#security' },
  { label: 'Customers', href: '#customers' },
] as const

export const FOOTER_LINKS = [
  { label: 'Platform', href: '#platform' },
  { label: 'Intelligence', href: '#intelligence' },
  { label: 'Dealflow', href: '#dealflow' },
  { label: 'Security', href: '#security' },
  { label: 'Privacy', href: 'https://www.dgpt.io/privacy' },
  { label: 'Terms', href: 'https://www.dgpt.io/terms' },
  { label: 'Insights', href: 'https://www.dgpt.io/insights' },
  { label: 'StartupFuel', href: 'https://www.startupfuel.com' },
] as const

export const AUDIENCES = ['VC Funds', 'CVCs', 'Family Offices', 'Accelerators', 'Innovation Teams']

/** Organizations currently shown on dgpt.io. Rendered as monochrome wordmarks. */
export const ECOSYSTEM_LOGOS = [
  {
    name: 'Techstars',
    src: 'https://cdn.magicpatterns.com/uploads/ic2swpBvjg2QntGFRw2efH/64660d70c2eb8d2664ed7518_Techstars_Logo_Primary_(2).png',
    scale: 1.35,
  },
  {
    name: 'The World Bank',
    src: 'https://cdn.magicpatterns.com/uploads/h4TWuv6FSmSQMxCzj28ZV8/world-bank-logo_(1).png',
    scale: 1.9,
  },
  {
    name: 'Village Capital',
    src: 'https://cdn.magicpatterns.com/uploads/7XE4WsCbnnYMJsWUCZ3P5B/Website_Village_Capital_Logo_(2).png',
    scale: 1.15,
  },
  {
    name: 'Blue Vision Capital',
    src: 'https://cdn.magicpatterns.com/uploads/pHSn8xj5AwkNYeXsZKuXoT/screenshot-2022-12-22-at-10-23-55-am_(2).png',
    scale: 2.1,
  },
  {
    name: 'Measured Capital',
    src: 'https://cdn.magicpatterns.com/uploads/xfZnhsmVckaAoMyy5uu4Vp/Asset2B1_(2).webp',
    scale: 0.95,
  },
  {
    name: 'IBM',
    src: 'https://cdn.magicpatterns.com/uploads/2TNBFD5JzP9FgnfGE4rfjN/ibm-logo_(2).webp',
    scale: 0.65,
  },
  {
    name: 'Sony',
    src: 'https://cdn.magicpatterns.com/uploads/5m2cCQ6UnAKCWQZzTuBbji/sony-logo_(1).svg',
    scale: 0.8,
  },
] as const

export const DGPT_LOGO_SRC =
  'https://cdn.magicpatterns.com/uploads/izXwPzD6vEjAVuwXw1X7o8/dgptlogo-DugKi5EU.png'

export const MOAT = {
  generic: [
    'Summarize a pitch deck',
    'Search public information',
    'Draft an investment memo',
    'Generate market research',
    'Build dashboards',
    'Generate diligence questions',
    'Create simple workflows',
  ],
  dgpt: [
    'Proprietary startup operating data',
    'Fund-specific investment intelligence',
    'Investment thesis matching',
    'Cross-company benchmarking',
    'Historical diligence intelligence',
    'Global startup sourcing',
    'Structured investment workflows',
    'Growing proprietary dealflow',
    'Intelligence across 500+ startup communities',
  ],
} as const

export const FLYWHEEL_STAGES = [
  'Global Startup Communities',
  'New Startup Dealflow',
  'Structured Company Data',
  'Diligence + Benchmarking',
  'Fund Intelligence',
  'Thesis Matching',
  'Investor Decisions',
  'Better Discovery',
] as const

export const INTELLIGENCE_LAYERS = [
  {
    eyebrow: 'Startup Intelligence',
    title: 'Understand the company.',
    items: ['Operating metrics', 'Team', 'Market', 'Traction', 'Business model', 'Competition', 'Moat', 'Financials', 'Risk', 'Comparables'],
  },
  {
    eyebrow: 'Fund Intelligence',
    title: 'Understand what matters to your fund.',
    items: ['Investment thesis', 'Stage', 'Geography', 'Sector', 'Cheque size', 'Ownership targets', 'Strategic fit', 'Historical preferences', 'Portfolio context', 'Custom scoring criteria'],
  },
  {
    eyebrow: 'Network Intelligence',
    title: "Discover what you haven't seen.",
    items: ['Global startup communities', 'Accelerators', 'Incubators', 'University ecosystems', 'Innovation hubs', 'Startup programs', 'Events', 'Curated dealflow'],
  },
] as const

export const PRODUCT_STEPS = [
  {
    id: 'capture',
    number: '01',
    title: 'Capture Every Deal',
    body: 'Branded founder portal per fund. Email forwarding. Google Drive and DocSend ingestion. Data rooms in any file format.',
    tags: ['Founder portals', 'Email', 'Pitch decks', 'Data rooms', 'Google Drive', 'DocSend', 'Applications'],
  },
  {
    id: 'match',
    number: '02',
    title: 'Match Against Your Thesis',
    body: "Every company is evaluated against your fund's specific investment mandate — not a generic VC framework.",
    tags: ['Fit Score', 'Thesis criteria', 'Per-fund configuration'],
  },
  {
    id: 'case',
    number: '03',
    title: 'Build the Investment Case',
    body: 'A tearsheet plus structured reports per deal — customizable and PDF-exportable for your Investment Committee.',
    tags: ['Tearsheet', 'Market Intelligence', 'Deal Brief', 'Business Risk', 'Metrics', 'Comparables', 'Gap Analysis', 'Investment Memo'],
  },
  {
    id: 'collaborate',
    number: '04',
    title: 'Collaborate',
    body: 'One workspace per thematic fund. Team reviews, voting, and shared diligence on every deal.',
    tags: ['Team review', 'Voting', 'Comments', 'Deal pipeline', 'IC preparation', 'Shared diligence'],
  },
  {
    id: 'radar',
    number: '05',
    title: 'Discover New Companies',
    body: "Your inbox shows you the deals you already know. Deal Radar helps surface the ones you don't — matching companies from StartupFuel's global ecosystem against your investment thesis.",
    tags: ['Deal Radar', 'Accelerators', 'Conferences', 'Innovation hubs'],
  },
] as const

const FIT_SCORES_IMAGE = {
  src: 'https://cdn.magicpatterns.com/uploads/525STsXGev6NbGHrtojpY4/Screenshot_2026-10-21_at_1.04.53_PM.png',
  alt: 'DiligenceGPT Fit Scores panel ranking five deals against a fund thesis',
  width: 908,
  height: 1026,
}

/** Product screenshots shown to the right of story steps 02–05. */
export const STEP_IMAGES: Partial<Record<(typeof PRODUCT_STEPS)[number]['id'], typeof FIT_SCORES_IMAGE & { crop?: { aspect: string; position: string } }>> = {
  match: {
    src: 'https://cdn.magicpatterns.com/uploads/5aZEJcNKtvoNdzz35A2Mi6/Screenshot_2026-10-21_at_1.06.06_PM.png',
    alt: 'DiligenceGPT pipeline table with a fit score for every deal against the fund thesis',
    width: 2360,
    height: 1440,
    crop: { aspect: '2360 / 860', position: 'bottom' },
  },
  case: {
    src: 'https://cdn.magicpatterns.com/uploads/u8cBCRgeaDagKdr5XGZqzs/Screenshot_2026-10-21_at_1.05.34_PM.png',
    alt: 'DiligenceGPT Deal Memo and Business Risk report cards for a company',
    width: 1598,
    height: 1174,
  },
  collaborate: {
    src: 'https://cdn.magicpatterns.com/uploads/8P7hDSv2BzXq6u6teCLdsy/Screenshot_2026-10-21_at_1.30.02_PM.png',
    alt: 'DiligenceGPT shared Kanban pipeline with deals moving between diligence stages',
    width: 2266,
    height: 1312,
  },
  radar: {
    src: 'https://cdn.magicpatterns.com/uploads/qcyJyPGrRHtCKtC3TBXvxJ/Screenshot_2026-10-21_at_1.19.45_PM.png',
    alt: 'DiligenceGPT investment thesis settings used to match and score newly surfaced companies',
    width: 2436,
    height: 1506,
    crop: { aspect: '2436 / 790', position: 'bottom' },
  },
}

export const INVESTOR_QUESTIONS = [
  'Should we look at this company?',
  'Does it actually fit our thesis?',
  "How does it compare to everything else we've seen?",
  'What risks are we missing?',
  'What should we diligence next?',
  'What information is missing?',
  "Which companies should we know about that haven't reached our inbox yet?",
] as const

export const LIFECYCLE = ['Source', 'Screen', 'Diligence', 'IC', 'Invest', 'Portfolio', 'LP Reporting'] as const

/** Security claims as currently stated on dgpt.io — do not upgrade wording without verification. */
export const SECURITY_ITEMS = [
  {
    title: 'SOC 2 in progress',
    body: 'Independent audit underway. We follow SOC 2 controls today; certification on track.',
  },
  {
    title: 'No model training',
    body: 'Your deal data is never used to train our models or any third-party model.',
  },
  {
    title: 'Tenant isolation',
    body: 'Strict tenant isolation across workspaces and funds.',
  },
  {
    title: 'Anonymization tools',
    body: 'Redact PII and confidential identifiers per workspace.',
  },
  {
    title: 'Retention controls',
    body: 'Configurable data retention with automatic deletion on schedule.',
  },
  {
    title: 'AWS-hosted, hardened',
    body: 'KMS-encrypted at rest, secrets in AWS Secrets Manager, IAM-restricted access.',
  },
] as const

/** Approved testimonials from dgpt.io. Relationship type preserved. */
export const TESTIMONIALS = [
  {
    quote:
      'Build In Tulsa used DiligenceGPT to generate standardized, unbiased ratings with company assessments, which helped them understand their investability. They were able to filter, review, and provide feedback to the startups entering their program.',
    person: 'Montana',
    role: 'Program Director',
    organization: 'Build In Tulsa',
    relationship: 'Accelerator program',
  },
  {
    quote:
      'The diligence service provided is so enriching, having a support team to help filter startup deal flow at the top of the funnel helps increase what we can see.',
    person: 'Meir Rabkin',
    role: 'GP',
    organization: 'Blue Vision Capital',
    relationship: 'Venture fund',
  },
  {
    quote:
      'The SF diligence gives us a 3rd party, unbiased way to compare our team’s work with experts. Their process is thorough for diligence.',
    person: 'Sumit',
    role: 'Director',
    organization: 'Rogers Accelerator',
    relationship: 'Accelerator',
  },
  {
    quote:
      "GX Ventures required StartupFuel's support to help launch their fund, investor relations, & LP outreach. They were also looking for an AI solution to warehouse deals, collect data room docs, and generate reports.",
    person: 'A.J. Noronha, CFA',
    role: 'Managing Director',
    organization: 'GX Ventures',
    relationship: 'Fund launch & platform',
  },
] as const

export const PILOT_NOTE = 'Pilot in progress with Sony Innovation Fund.'

export const GENERATIONS = [
  {
    label: 'Generation 1',
    title: 'CRM + Databases',
    quote: 'Store what your team already knows.',
    items: ['Contacts', 'Pipelines', 'Notes', 'Databases'],
  },
  {
    label: 'Generation 2',
    title: 'Generative AI',
    quote: 'Ask questions about information.',
    items: ['Summarization', 'Search', 'Chat', 'Memo generation'],
  },
  {
    label: 'Generation 3',
    title: 'Venture Intelligence',
    quote: 'Continuously understand, evaluate, compare and discover opportunities.',
    items: ['Startup Intelligence', 'Fund Intelligence', 'Network Intelligence', 'Agentic Workflows'],
  },
] as const

export const SEO = {
  title: 'DiligenceGPT™ | Diligence at the Speed of Dealflow',
  description:
    'DiligenceGPT is the intelligence layer for venture capital. Screen, diligence, compare and discover startups with proprietary startup, fund and network intelligence. Book a demo.',
  url: 'https://www.dgpt.io/',
} as const

/** Illustrative product data used inside UI modules. */
export const DEMO_DEAL = {
  name: 'SentinelForge',
  sector: 'Series B · Healthcare',
  summary: 'AI-powered fraud detection for fintech infrastructure',
  fitScore: 87,
  criteria: [
    { label: 'Healthcare AI', met: true },
    { label: 'Series B+', met: true },
    { label: 'Founder with prior exit', met: true },
    { label: 'ARR > $10M', met: false, value: '$8.4M' },
    { label: 'Multi-region traction', met: true },
  ],
} as const

export const DEMO_RADAR = [
  { name: 'Veridian Health', meta: 'Series A · Healthcare AI', score: 92 },
  { name: 'Corvid Labs', meta: 'Seed · Vertical AI', score: 84 },
  { name: 'Perch', meta: 'Series B · B2B SaaS', score: 76 },
  { name: 'Northstack', meta: 'Series A · Dev tools', score: 71 },
] as const

export const DEMO_REPORTS = ['Tearsheet', 'Market Intel', 'Deal Brief', 'Business Risk', 'Metrics', 'Comparables', 'Gap Analysis', 'Investment Memo'] as const
